import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Briefcase,
  Building2,
  Calendar,
  BarChart3,
  DollarSign,
  MessageSquare,
  X,
  Target,
  Brain,
  FileEdit,
  Star,
  Handshake,
  SprayCan,
  Plug,
  BrushCleaning,
  Workflow,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useAppAuth } from "@/contexts/AuthContext";
import { AppPermission } from "@/types/auth";

const navigation = [
  {
    name: "Dashboard",
    icon: LayoutDashboard,
    current: true,
    href: "/dashboard",
  },
  {
    name: "Leads",
    icon: Target,
    current: false,
    href: "/leads",
    permission: "leads:read" as AppPermission,
  },
  {
    name: "Bids",
    icon: DollarSign,
    current: false,
    href: "/bids",
    permission: "bids:read" as AppPermission,
  },
  {
    name: "Documents",
    icon: FileText,
    current: false,
    href: "/documents",
  },
  {
    name: "Ratings & Reviews",
    icon: Star,
    current: false,
    href: "/reviews",
    permission: "reviews:read" as AppPermission,
  },
  {
    name: "Clients",
    icon: Building2,
    current: false,
    href: "/clients",
    permission: "clients:read" as AppPermission,
    children: [
      { name: "All Clients", href: "/clients", permission: "clients:read_all" as AppPermission },
      {
        name: "Service Locations",
        href: "/clients/locations",
        permission: "locations:read" as AppPermission,
      },
      {
        name: "Location Onboarding",
        href: "/clients/onboarding",
        permission: "clients:onboarding" as AppPermission,
      },
    ],
  },
  {
    name: "Employees",
    icon: Users,
    current: false,
    href: "/employees",
    permission: "team:read" as AppPermission,
  },
  {
    name: "Subcontractors",
    icon: Handshake,
    current: false,
    href: "/subcontractors",
  },
  {
    name: "Service Projects",
    icon: BrushCleaning,
    current: false,
    href: "/project-schedules",
    permission: "locations:read" as AppPermission,
  },
  {
    name: "Equipment",
    icon: Plug,
    current: false,
    href: "/equipment",
  },
  {
    name: "Master Supply",
    icon: SprayCan,
    current: false,
    href: "/supplies",
  },
  {
    name: "AI Knowledge Base",
    icon: Brain,
    current: false,
    href: "/knowledge-base",
    permission: "knowledge_base:read" as AppPermission,
  },


  {
    name: "Company",
    icon: Briefcase,
    current: false,
    href: "/company-profile",
  },
  {
    name: "Calendar",
    icon: Calendar,
    current: false,
    href: "/calendar",
    children: [
      { name: "My Calendar", href: "/calendar" },
      { name: "Team Calendar", href: "/calendar/team", permission: "team:read" as AppPermission },
    ],
  },
];

const bottomNavigation = [
  {
    name: "Settings",
    icon: Settings,
    href: "/settings",
    permission: "settings:read" as AppPermission,
    children: [
      { name: "General Settings", href: "/settings", permission: "settings:read" as AppPermission },
      {
        name: "Connections",
        href: "/settings/connections",
        permission: "settings:read" as AppPermission,
      },
      { name: "Users & Roles", href: "/settings/users", permission: "team:read" as AppPermission },
      { name: "Company", href: "/settings/company", permission: "settings:read" as AppPermission },
      {
        name: "Billing",
        href: "/settings/billing",
        permission: "organization:billing" as AppPermission,
      },
    ],
  },
  {
    name: "Help & Support",
    icon: HelpCircle,
    href: "/dashboard/help",
    children: [
      { name: "Success Hub & Guides", href: "/success-hub" },
      { name: "Frequently Asked Questions", href: "/success-hub/faq" },
      { name: "Contact Support", href: "/dashboard/help/support" },
    ],
  },
];

export function DashboardSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAppAuth();

  // Initialize collapsed state from sessionStorage, defaulting to true (collapsed)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = sessionStorage.getItem("sidebar-collapsed-state");
      return stored !== null ? JSON.parse(stored) : true;
    } catch (error) {
      console.error("Failed to load sidebar state from sessionStorage:", error);
      return true;
    }
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number } | null>(null);
  const [openSection, setOpenSection] = useState<string | null>(() => {
    // Initialize with the correct section based on current path
    if (location.pathname === "/dashboard" || location.pathname === "/analytics") {
      return "Dashboards";
    } else if (location.pathname.startsWith("/clients/") || location.pathname === "/clients") {
      return "Clients";
    } else if (location.pathname.startsWith("/reviews") || location.pathname === "/reviews") {
      return "Ratings & Reviews";
    } else if (
      location.pathname.startsWith("/project-schedules") ||
      location.pathname === "/project-schedules"
    ) {
      return "Service Projects";
    } else if (location.pathname.startsWith("/calendar/") || location.pathname === "/calendar") {
      return "Calendar";
    } else if (location.pathname === "/company-profile" || location.pathname === "/employees") {
      return "Company";
    } else if (
      location.pathname.startsWith("/automation-hub") ||
      location.pathname === "/automation-hub" ||
      location.pathname.startsWith("/workflows") ||
      location.pathname === "/workflows"
    ) {
      return "Automation Hub";
    } else if (location.pathname.startsWith("/dashboard/settings/")) {
      return "Settings";
    } else if (location.pathname.startsWith("/dashboard/help/")) {
      return "Help & Support";
    }
    return null;
  });

  // Persist collapsed state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem("sidebar-collapsed-state", JSON.stringify(collapsed));
    } catch (error) {
      console.error("Failed to save sidebar state to sessionStorage:", error);
    }
  }, [collapsed]);

  // Tooltip handlers
  const handleMouseEnter = (itemName: string, event: React.MouseEvent) => {
    if (collapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoveredItem(itemName);
      setTooltipPosition({ top: rect.top + rect.height / 2 });
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
    setTooltipPosition(null);
  };

  const isCurrentPath = (path: string) => {
    return (
      location.pathname === path ||
      (path === "/dashboard" &&
        (location.pathname === "/dashboard" || location.pathname === "/analytics")) ||
      (path === "/leads" && location.pathname.startsWith("/leads")) ||
      (path === "/clients" && location.pathname.startsWith("/clients/")) ||
      (path === "/project-schedules" && location.pathname.startsWith("/project-schedules")) ||
      (path === "/subcontractors" && location.pathname.startsWith("/subcontractors/")) ||
      (path === "/calendar" && location.pathname.startsWith("/calendar/")) ||
      (path === "/messages" && location.pathname.startsWith("/messages")) ||
      (path === "/employees" && location.pathname.startsWith("/employees")) ||
      (path === "/agent-mode" && (location.pathname.startsWith("/agent-mode") || location.pathname.startsWith("/automation-hub"))) ||
      (path === "/bids" && location.pathname.startsWith("/bids")) ||
      (path === "#" &&
        (location.pathname === "/company-profile" || location.pathname.startsWith("/company/"))) ||
      (path === "/dashboard/settings" && location.pathname.startsWith("/dashboard/settings/")) ||
      (path === "/dashboard/help" && location.pathname.startsWith("/dashboard/help/"))
    );
  };

  const isChildPathActive = (childPath: string) => {
    // Special case for Dashboard children
    if (childPath === "/dashboard" && location.pathname === "/dashboard") {
      return true;
    }
    if (childPath === "/analytics" && location.pathname === "/analytics") {
      return true;
    }
    // Special case for Service Locations - only highlight when exact match
    if (childPath === "/clients/locations" && location.pathname === "/clients/locations") {
      return true;
    }
    // Special case for All Clients - don't highlight when on Service Locations, Onboarding, or Reviews
    if (
      childPath === "/clients" &&
      (location.pathname === "/clients/locations" ||
        location.pathname.startsWith("/clients/locations/") ||
        location.pathname === "/clients/onboarding" ||
        location.pathname.startsWith("/clients/onboarding/") ||
        location.pathname === "/clients/reviews" ||
        location.pathname.startsWith("/clients/reviews/"))
    ) {
      return false;
    }
    // Special case for Service Locations - highlight when on locations list or detail pages
    if (
      childPath === "/clients/locations" &&
      (location.pathname === "/clients/locations" ||
        location.pathname.startsWith("/clients/locations/"))
    ) {
      return true;
    }
    // Special case for Onboarding - highlight when on onboarding list or detail pages
    if (
      childPath === "/clients/onboarding" &&
      (location.pathname === "/clients/onboarding" ||
        location.pathname.startsWith("/clients/onboarding/"))
    ) {
      return true;
    }
    // Special case for Ratings & Reviews - highlight when on reviews list or detail pages
    if (
      childPath === "/reviews" &&
      (location.pathname === "/reviews" || location.pathname.startsWith("/reviews/"))
    ) {
      return true;
    }

    // Special case for Calendar sections
    if (childPath === "/calendar" && location.pathname === "/calendar") {
      return true;
    }
    if (childPath === "/calendar" && location.pathname.startsWith("/calendar/")) {
      return false;
    }
    if (
      childPath === "/calendar/service-scheduling" &&
      location.pathname === "/calendar/service-scheduling"
    ) {
      return true;
    }
    if (childPath === "/calendar/appointments" && location.pathname === "/calendar/appointments") {
      return true;
    }
    if (childPath === "/calendar/team" && location.pathname === "/calendar/team") {
      return true;
    }
    // Special case for General Settings - only highlight when exactly on /settings
    // not when on /settings/users, /settings/connections, etc.
    if (childPath === "/settings" && location.pathname !== "/settings") {
      return false;
    }
    // Special case for Automation Hub hub - only highlight when exactly on /automation-hub
    if (childPath === "/automation-hub" && location.pathname !== "/automation-hub") {
      return false;
    }
    return location.pathname === childPath || location.pathname.startsWith(childPath + "/");
  };

  // Adjust button classes for better touch targets on mobile
  const buttonClasses = collapsed
    ? "w-full h-12 justify-center px-0 text-foreground hover:bg-gray-100"
    : "w-full h-12 justify-start gap-2 px-3 text-foreground hover:bg-gray-100";

  // Filter navigation items based on permissions
  const filterNavigationItem = (item: any) => {
    // If item has permission requirement, check it
    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }

    // If item has children, filter them and only show parent if it has visible children or no permission requirement
    if (item.children) {
      const visibleChildren = item.children.filter(
        (child: any) => !child.permission || hasPermission(child.permission)
      );

      // Show parent if it has no permission requirement or if it has visible children
      return !item.permission || visibleChildren.length > 0;
    }

    return true;
  };

  const filteredNavigation = navigation.filter(filterNavigationItem);
  const filteredBottomNavigation = bottomNavigation.filter(filterNavigationItem);

  return (
    <div
      className={cn(
        "z-50 h-full border-r bg-white transition-all duration-300",
        collapsed ? "w-16" : "w-64 sm:w-72"
      )}
    >
      <TooltipProvider>
        <nav className="flex h-full flex-col overflow-hidden">
          <div className={cn("flex h-16 items-center border-b border-gray-200 px-4", collapsed ? "justify-center" : "justify-between")}>
            <img
              src={collapsed ? "/mc-icon.svg" : "/logo.svg"}
              alt="Logo"
              className={cn("w-auto", collapsed ? "h-7" : "h-8")}
            />
            {/* Close button only visible on small screens */}
            <Button
              variant="ghost"
              size="icon"
              className="sm:hidden"
              onClick={() => document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"))}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto overflow-x-hidden py-4">
            {filteredNavigation.map((item) => (
              <div key={item.name} className="mb-1">
                {collapsed ? (
                  item.children ? (
                    <div className="relative">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            className={cn(
                              buttonClasses,
                              isCurrentPath(item.href) && "bg-blue-50 text-blue-700"
                            )}
                            onMouseEnter={(e) => handleMouseEnter(item.name, e)}
                            onMouseLeave={handleMouseLeave}
                          >
                            <item.icon
                              className={cn("h-5 w-5", isCurrentPath(item.href) && "text-blue-700")}
                            />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent side="right" className="w-56 p-2">
                          <div className="space-y-1">
                            {item.children
                              .filter(
                                (child: any) => !child.permission || hasPermission(child.permission)
                              )
                              .map((child) => (
                                <Button
                                  key={child.name}
                                  variant="ghost"
                                  className={cn(
                                    "h-10 w-full justify-start text-sm",
                                    isChildPathActive(child.href) && "bg-blue-50 text-blue-700"
                                  )}
                                  onClick={() => navigate(child.href)}
                                >
                                  {child.name}
                                </Button>
                              ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            buttonClasses,
                            isCurrentPath(item.href) && "bg-blue-50 text-blue-700"
                          )}
                          onClick={() => {
                            navigate(item.href);
                            // Close mobile sidebar when navigating
                            if (window.innerWidth < 640) {
                              document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"));
                            }
                          }}
                        >
                          <item.icon
                            className={cn("h-5 w-5", isCurrentPath(item.href) && "text-blue-700")}
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>{item.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                ) : (
                  <Button
                    variant="ghost"
                    className={cn(
                      buttonClasses,
                      isCurrentPath(item.href) && "bg-blue-50 text-blue-700",
                      isUpdating && "cursor-not-allowed opacity-50"
                    )}
                    disabled={isUpdating}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (isUpdating) return;

                      if (item.children) {
                        setIsUpdating(true);
                        setOpenSection((prev) => (prev === item.name ? null : item.name));
                        // Reset updating state after a short delay
                        setTimeout(() => setIsUpdating(false), 150);
                      } else {
                        navigate(item.href);
                        // Close mobile sidebar when navigating
                        if (window.innerWidth < 640) {
                          document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"));
                        }
                      }
                    }}
                  >
                    <item.icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        isCurrentPath(item.href) && "text-blue-700"
                      )}
                    />
                    <span className="truncate">{item.name}</span>
                    {item.children && (
                      <ChevronDown
                        className={cn(
                          "ml-auto h-4 w-4 flex-shrink-0 transition-transform",
                          openSection === item.name && "rotate-180"
                        )}
                      />
                    )}
                  </Button>
                )}
                {!collapsed && openSection === item.name && item.children && (
                  <div className="space-y-1 py-1 pl-10">
                    {item.children
                      .filter((child: any) => !child.permission || hasPermission(child.permission))
                      .map((child) => (
                        <Button
                          key={child.name}
                          variant="ghost"
                          className={cn(
                            "h-10 w-full justify-start text-sm text-gray-600 hover:text-gray-900",
                            isChildPathActive(child.href) && "bg-blue-50 text-blue-700"
                          )}
                          onClick={() => {
                            navigate(child.href);
                            // Keep the section open when navigating to a child
                            setOpenSection(item.name);
                            // Close mobile sidebar when navigating
                            if (window.innerWidth < 640) {
                              document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"));
                            }
                          }}
                        >
                          {child.name}
                        </Button>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="relative mt-auto space-y-2 border-t bg-white px-2 py-4">
            {filteredBottomNavigation.map((item) => (
              <div key={item.name} className="mb-1">
                {collapsed ? (
                  item.children ? (
                    <div className="relative">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            className={cn(
                              buttonClasses,
                              isCurrentPath(item.href) && "bg-blue-50 text-blue-700"
                            )}
                            onMouseEnter={(e) => handleMouseEnter(item.name, e)}
                            onMouseLeave={handleMouseLeave}
                          >
                            <item.icon
                              className={cn("h-5 w-5", isCurrentPath(item.href) && "text-blue-700")}
                            />
                          </Button>
                        </PopoverTrigger>
                        {item.children && (
                          <PopoverContent side="right" className="w-56 p-2">
                            <div className="space-y-1">
                              {item.children
                                .filter(
                                  (child: any) =>
                                    !child.permission || hasPermission(child.permission)
                                )
                                .map((child) => (
                                  <Button
                                    key={child.name}
                                    variant="ghost"
                                    className={cn(
                                      "h-10 w-full justify-start text-sm",
                                      isChildPathActive(child.href) && "bg-blue-50 text-blue-700"
                                    )}
                                    onClick={() => navigate(child.href)}
                                  >
                                    {child.name}
                                  </Button>
                                ))}
                            </div>
                          </PopoverContent>
                        )}
                      </Popover>
                    </div>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className={cn(
                            buttonClasses,
                            isCurrentPath(item.href) && "bg-blue-50 text-blue-700"
                          )}
                          onClick={() => {
                            navigate(item.href);
                            // Close mobile sidebar when navigating
                            if (window.innerWidth < 640) {
                              document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"));
                            }
                          }}
                        >
                          <item.icon
                            className={cn("h-5 w-5", isCurrentPath(item.href) && "text-blue-700")}
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>{item.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                ) : (
                  <Button
                    variant="ghost"
                    className={cn(
                      buttonClasses,
                      isCurrentPath(item.href) && "bg-blue-50 text-blue-700",
                      isUpdating && "cursor-not-allowed opacity-50"
                    )}
                    disabled={isUpdating}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (isUpdating) return;

                      if (item.children) {
                        setIsUpdating(true);
                        setOpenSection((prev) => (prev === item.name ? null : item.name));
                        // Reset updating state after a short delay
                        setTimeout(() => setIsUpdating(false), 150);
                      } else {
                        navigate(item.href);
                        // Close mobile sidebar when navigating
                        if (window.innerWidth < 640) {
                          document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"));
                        }
                      }
                    }}
                  >
                    <item.icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        isCurrentPath(item.href) && "text-blue-700"
                      )}
                    />
                    <span className="truncate">{item.name}</span>
                    {item.children && (
                      <ChevronDown
                        className={cn(
                          "ml-auto h-4 w-4 flex-shrink-0 transition-transform",
                          openSection === item.name && "rotate-180"
                        )}
                      />
                    )}
                  </Button>
                )}
                {!collapsed && openSection === item.name && item.children && (
                  <div className="space-y-1 py-1 pl-10">
                    {item.children
                      .filter((child: any) => !child.permission || hasPermission(child.permission))
                      .map((child) => (
                        <Button
                          key={child.name}
                          variant="ghost"
                          className={cn(
                            "h-10 w-full justify-start text-sm text-gray-600 hover:text-gray-900",
                            isChildPathActive(child.href) && "bg-blue-50 text-blue-700"
                          )}
                          onClick={() => {
                            navigate(child.href);
                            // Keep the section open when navigating to a child
                            setOpenSection(item.name);
                            // Close mobile sidebar when navigating
                            if (window.innerWidth < 640) {
                              document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"));
                            }
                          }}
                        >
                          {child.name}
                        </Button>
                      ))}
                  </div>
                )}
              </div>
            ))}
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(buttonClasses)}
                    onClick={() => setCollapsed(!collapsed)}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Expand sidebar</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                variant="ghost"
                className={cn(buttonClasses)}
                onClick={() => setCollapsed(!collapsed)}
              >
                <ChevronLeft className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">Collapse</span>
              </Button>
            )}
          </div>
        </nav>
      </TooltipProvider>

      {/* Manual tooltip for items with children */}
      {hoveredItem && collapsed && tooltipPosition && (
        <div
          className="pointer-events-none fixed left-16 z-50 -translate-y-1/2 transform"
          style={{ top: tooltipPosition.top }}
        >
          <div className="whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-sm text-white shadow-lg">
            {hoveredItem}
          </div>
        </div>
      )}
    </div>
  );
}
